import AddCircleOutlineRounded from "@mui/icons-material/AddCircleOutlineRounded";
import Clear from "@mui/icons-material/Clear";
import DeleteOutline from "@mui/icons-material/DeleteOutline";
import { Button, Stack, Typography, useTheme } from "@mui/material";
import { GridActionsCellItem, GridColDef, GridRowSelectionModel } from "@mui/x-data-grid-premium";
import { DataGrid } from "components/DataGrid";
import { ContactReportRole, Facility } from "generated-graphql/graphql";
import { useCallback, useMemo, useState } from "react";

import ContactFacilitiesDialog from "./ContactFacilitiesDialog";
import { ContactRoleChipLegend, ContactRoleChipList } from "./ContactRoleChip";

type AssociatedFacilitiesProps = {
  facilityContacts: AssociatedFacilityContact[];
  onUpdate: (updates: AssociatedFacilityContact[]) => void;
  onRemove: (indexToRemove: number) => void;
};

export type AssociatedFacilityContact = {
  id: string;
  facility: Pick<Facility, "id" | "name" | "streetAddress1" | "city" | "state" | "zip">;
  reportingRoles: ContactReportRole[];
};

export default function AssociatedFacilities({
  facilityContacts,
  onUpdate,
  onRemove,
}: AssociatedFacilitiesProps) {
  const theme = useTheme();

  const [paginationModel, setPaginationModel] = useState({
    pageSize: 10,
    page: 0,
  });
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>({
    type: "include",
    ids: new Set(),
  });

  const columns: GridColDef<AssociatedFacilityContact>[] = [
    {
      field: "name",
      headerName: "Facility",
      flex: 1,
      valueGetter: (_, row) => row.facility.name,
      renderCell: ({ row }) => {
        const streetAddress = `${row.facility.streetAddress1}, ${row.facility.city}, ${row.facility.state} ${row.facility.zip}`;

        return (
          <Stack>
            <Typography variant="body2">{row.facility.name}</Typography>
            <Typography variant="caption">{streetAddress}</Typography>
          </Stack>
        );
      },
    },
    {
      field: "role",
      headerName: "Roles",
      flex: 0.5,
      renderCell: ({ row }) => <ContactRoleChipList roles={row.reportingRoles} />,
    },
    {
      field: "state",
      headerName: "State/Territory",
      flex: 0.25,
      valueGetter: (_, row) => row.facility.state,
    },
    {
      field: "actions",
      type: "actions",
      flex: 0.15,
      align: "left",
      getActions: ({ row }) => {
        const index = facilityContacts.findIndex((fc) => fc.facility.id === row.facility.id);
        const actions = [
          <GridActionsCellItem
            key="remove"
            onClick={() => {
              onRemove(index);
            }}
            label="Remove"
            icon={<Clear />}
          />,
        ];

        return actions;
      },
    },
  ];

  const selectedIds = useMemo(() => {
    if (rowSelectionModel.type === "include") {
      return Array.from(rowSelectionModel.ids) as string[];
    }
    return [];
  }, [rowSelectionModel]);

  const handleBulkRemove = useCallback(() => {
    if (selectedIds.length === 0) return;

    // Find indices for selected facility IDs, sorted from highest to lowest
    // to avoid index shifting issues when removing
    const indicesToRemove = selectedIds
      .map((facilityId) => facilityContacts.findIndex((fc) => fc.facility.id === facilityId))
      .filter((index) => index !== -1)
      .sort((a, b) => b - a);

    indicesToRemove.forEach((index) => {
      onRemove(index);
    });

    setRowSelectionModel({ type: "include", ids: new Set() });
  }, [selectedIds, facilityContacts, onRemove]);

  const hasSelection = selectedIds.length > 0;

  return (
    <Stack spacing={theme.spacing(2)}>
      <Stack direction="row" justifyContent="space-between" alignItems="end">
        <Typography variant="body1" fontWeight={500}>
          Associated Facilities
        </Typography>
        <Stack direction="row" spacing={1}>
          {hasSelection && (
            <Button
              size="small"
              variant="text"
              color="error"
              onClick={handleBulkRemove}
              startIcon={<DeleteOutline />}
            >
              Remove Selected ({selectedIds.length})
            </Button>
          )}
          <Button
            size="small"
            variant="text"
            onClick={() => setDialogOpen(true)}
            startIcon={<AddCircleOutlineRounded />}
          >
            Link Facilities
          </Button>
        </Stack>
      </Stack>

      <DataGrid
        autoHeight
        getRowHeight={() => "auto"}
        initialState={{
          sorting: {
            sortModel: [
              { field: "name", sort: "asc" },
              { field: "role", sort: "asc" },
            ],
          },
        }}
        columns={columns}
        rows={facilityContacts}
        getRowId={(row) => row.facility.id}
        checkboxSelection
        rowSelectionModel={rowSelectionModel}
        onRowSelectionModelChange={setRowSelectionModel}
        disableRowSelectionOnClick
        pagination={true}
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
      />

      <ContactRoleChipLegend />

      {dialogOpen && (
        <ContactFacilitiesDialog
          open={dialogOpen}
          facilityContacts={facilityContacts}
          onClose={() => {
            setDialogOpen(false);
          }}
          onSave={(updates) => {
            // Is there a better way to ensure we don't have duplicates?
            const contactsMap = new Map(facilityContacts.map((c) => [c.facility.id, c]));
            for (const update of updates) {
              contactsMap.set(update.facility.id, update);
            }
            onUpdate(Array.from(contactsMap.values()));
          }}
        />
      )}
    </Stack>
  );
}
